/**
 * 세번 (10-digit) 상세 패널 — CielHS 이미지 173/174 1:1 mirror (정보 구조).
 *
 * Props:
 *  - hsk: "4202111010"
 *  - lang: 'kor' | 'eng'
 *
 * 데이터 fetch: /api/onto/hsk-detail?hsk=NNNNNNNNNN&lang=...
 *
 * 7 section (Task 3: 1·2 — 품목번호 + 품명. 나머지는 후속 task):
 *  1. 품목번호 (hsk + 단위)
 *  2. 품명 (국문/영문/작성예시)
 *  3. 원산지 (Task 4)
 *  4. 관세 7종 (Task 4)
 *  5. FTA 5 그룹 (Task 5)
 *  6·7. 감면환급 + 내국세 (Task 6)
 *  8. 수출입요건 (Task 7)
 */
import React, { useState, useEffect, useMemo } from "react";
import AgencyContactList from "./AgencyContactList";
import {
  buildGoogleSearchUrl,
  getGoogleSearchOpenMode,
  openGoogleSearchPopup,
  trackGoogleSearchAction,
} from "./GoogleSearchActions";
import "./HskDetailPanel.css";
import SourceEvidenceLink, {
  amendmentEvidence,
  amendmentReasonText,
} from "./SourceEvidenceLink";
import AmendmentPdfViewerModal, {
  AmendmentPdfViewerButton,
} from "./AmendmentPdfViewer";
import LawGuide from "./LawGuide";
import { ReqLines } from "./reqTextFormat";
import { AgencyLink, LawLink, TelLink, openLink, reqGuideUrl } from "./reqLinks";
import { computeApplicableRates, ORIGIN_COUNTRIES } from "./originToFta";
import { resolveFtaByCode } from "./ftaAgreements";
import { classifyPsr, PSR_TONES } from "./psrTaxonomy";
import { CHAPTERS_97 } from "./chaptersData";
import {
  customsChecksByDirection,
  displayLegacyRequirements,
  structuredCustomsItems,
} from "./customsChecks";
import { TARIFF_PRIORITY } from "./tariffReference";

// 세번·세율 숫자 글꼴 — 본문 웹폰트(Pretendard)에 고정폭 숫자. 불러오지 않는 JetBrains Mono 는
// Mac 에서 Courier 로 떨어져 읽기 어려웠다(문의 #104).
const NUM_FONT = "var(--font-family-sans)";

/*
 * 세번 상세 미리 받기·기억(2026-09-28) — 서버가 처음 보는 세번은 약 1초 계산한다(두 번째부터 0.06초).
 * 지도에서 마디에 마우스를 올리거나 출처·자동 길의 세번을 미리 받아 두면 누를 때 바로 보인다. 받은 것은 이 창에서 기억한다(최근 200개).
 */
const detailCache = new Map();   // key → Promise<data>
const CACHE_MAX = 200;
function detailKey(hsk, lang) { return `${hsk}|${lang || "kor"}`; }
let cacheFetch = null;   // 요청 함수가 바뀌면(시험의 가짜 fetch 등) 기억을 비운다 — 운영에서는 바뀌지 않는다
function loadHskDetail(hsk, lang) {
  if (cacheFetch !== window.fetch) { detailCache.clear(); cacheFetch = window.fetch; }
  const key = detailKey(hsk, lang);
  if (detailCache.has(key)) return detailCache.get(key);
  const p = fetch(`/api/onto/hsk-detail?hsk=${hsk}&lang=${lang || "kor"}`)
    .then((r) => {
      // 404 = 현행 관세율표에 없는 세번(폐지·만료 포함) — 오류가 아니라 "없음"이다.
      if (r.status === 404) return Promise.reject(new Error("NOT_FOUND"));
      return r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`));
    });
  detailCache.set(key, p);
  // 실패는 기억하지 않는다 — 다음에 다시 받는다.
  p.catch(() => { if (detailCache.get(key) === p) detailCache.delete(key); });
  if (detailCache.size > CACHE_MAX) detailCache.delete(detailCache.keys().next().value);
  return p;
}
/** 누르기 전에 미리 받는다(실패는 조용히). */
export function prefetchHskDetail(hsk, lang = "kor") {
  if (!hsk || !/^[0-9]{10}$/.test(String(hsk))) return;
  try { loadHskDetail(String(hsk), lang).catch(() => {}); } catch (_) { /* fetch 없음 — 무시 */ }
}
export function resetHskDetailCache() { detailCache.clear(); }

export default function HskDetailPanel({ hsk, lang }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [rateDetail, setRateDetail] = useState(null); // 클릭한 세율 상세 팝업(CLHS식)
  const [amendmentPdf, setAmendmentPdf] = useState(null);
  const [origin, setOrigin] = useState(""); // FTA 칸의 원산지 선택(구 원산지별 적용세율)

  useEffect(() => {
    if (!hsk || !/^[0-9]{10}$/.test(hsk)) return;
    let alive = true;
    setLoading(true);
    setErr(null);
    loadHskDetail(hsk, lang)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setErr(String(e.message || e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [hsk, lang]);

  if (loading) {
    return (
      <div style={{ color: "var(--audit-text-muted)", fontSize: 12 }}>
        세번 상세 불러오는 중…
      </div>
    );
  }
  if (err === "NOT_FOUND") {
    return (
      <div
        style={{
          color: "var(--audit-text-muted)",
          fontSize: 12,
          padding: 10,
          border: "1px solid var(--audit-border, #ddd)",
          borderRadius: 4,
        }}
      >
        현행 관세율표(2026년 기준)에 없는 세번입니다 (hsk={hsk}).
        폐지·개정으로 만료된 코드일 수 있습니다 — 좌측 세번표에서 현행 세분을
        확인해 주세요.
      </div>
    );
  }
  if (err) {
    return (
      <div
        style={{
          color: "#d93025",
          fontSize: 12,
          padding: 10,
          border: "1px solid #d93025",
          borderRadius: 4,
        }}
      >
        오류: {err}
      </div>
    );
  }
  if (!data) {
    return <div style={emptyStyle}>세번 데이터 없음 (hsk={hsk})</div>;
  }

  const hasNational = !!(data.national_group || data.national_tree);
  const req = displayLegacyRequirements(data);
  const hasReq =
    structuredCustomsItems(data).length > 0 ||
    (data.law_requirements || []).length > 0 ||
    (req &&
      ((req.import || []).length > 0 ||
        (req.export || []).length > 0 ||
        (req.strategic || []).length > 0 ||
        (req.other != null && String(req.other).trim() !== "")));
  const ex = data.popum ? `CASE OF ${String(data.popum).replace(/^OF\s+/i, "")}` : null;

  return (
    // 문의 #104 · CLHS 대조(2026-09-26) — 카드 12개를 한 장의 표로. 왼쪽 열이 섹션, 둘째 열이 소항목.
    // 관세·FTA 는 CLHS 처럼 "이름(코드): 세율" 한 줄씩. 관세율 상세(전체 세율표)·원산지별 적용세율 칸은
    // 관세·FTA 줄에 합쳤다(기간·단위세액은 그 줄에, 우선순위는 [적용순서 안내], 원산지 선택은 FTA 첫 줄).
    <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 760, width: "100%" }}>
      <SectionAmendment data={data} onOpenPdf={setAmendmentPdf} />
      <div className="hsk-sheet-wrap">
        <table className="hsk-sheet" data-testid="hsk-sheet">
          <tbody>
            <SheetSection label="품목번호" rows={[{ content: <SheetHeader data={data} /> }]} />
            <SheetSection
              label="품명"
              rows={[
                { k: "국문", content: data.name_ko || "—" },
                { k: "영문", content: data.popum || "—" },
                ...(ex
                  ? [{ k: "작성예시", content: <span className="hsk-muted"><i>{ex}</i> <span className="hsk-faint">(작성기준 데이터 별도)</span></span> }]
                  : []),
              ]}
            />
            <SheetSection label="관세" rows={[{ content: <SheetGeneralRates data={data} onRate={setRateDetail} /> }]} />
            <SheetSection label="FTA" rows={ftaRows(data, setRateDetail, origin, setOrigin)} />
            {hasNational && <SheetSection label="국내세분" rows={[{ content: <SectionNationalGroup data={data} />, body: true }]} />}
            <SheetSection label="원산지" rows={[{ content: <SectionOrigin data={data} />, body: true }]} />
            <SheetSection label="감면·환급" rows={[{ content: <SectionRefund data={data} />, body: true }]} />
            <SheetSection label="내국세" rows={[{ content: <SectionInternalTax data={data} />, body: true }]} />
            {hasReq && <SheetSection label="요건" rows={requirementRows(data)} />}
          </tbody>
        </table>
      </div>
      {rateDetail && (
        <RateDetailModal
          rate={rateDetail}
          hsk={data.hsk}
          onClose={() => setRateDetail(null)}
        />
      )}
      {amendmentPdf && (
        <AmendmentPdfViewerModal
          hsk={data.hsk}
          item={amendmentPdf}
          onClose={() => setAmendmentPdf(null)}
        />
      )}
    </div>
  );
}

// 세율 클릭 상세 팝업 (CLHS식) — 협정명·세율·부호 + 적용안내 + 관세청 원문 deep-link.
function RateDetailModal({ rate, hsk, onClose }) {
  const isFta = rate.kind === "fta";
  const [showOriginal, setShowOriginal] = useState(false); // 관세청-유사 원문 표 뷰
  // ⚠ resolveFtaByCode 는 매 호출 새 객체 → useEffect([ag,...]) 가 매 렌더 재실행되어
  //   setPsr(null)↔setPsr(data) 무한루프(영구 "불러오는 중"+endpoint 반복호출)였음. 메모이즈로 고정.
  const ag = useMemo(
    () => (isFta ? resolveFtaByCode(rate.code) : null),
    [isFta, rate.code],
  );
  const val = rate.value != null ? `${rate.value}%` : rate.raw ? rate.raw : "—";
  // 원산지결정기준(PSR) — 관세청 FTA 포털에서 온디맨드로 가져와 inline 표시.
  const [psr, setPsr] = useState(null); // null=로딩중 / {available, psr_ko, ...}
  useEffect(() => {
    if (!ag || !ag.ftaId || !hsk) return;
    const hs6 = String(hsk).slice(0, 6);
    let alive = true;
    setPsr(null);
    fetch(`/api/onto/fta-psr?ftaId=${ag.ftaId}&hs6=${hs6}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => alive && setPsr(d || { available: false }))
      .catch(() => alive && setPsr({ available: false }));
    return () => {
      alive = false;
    };
  }, [ag, hsk]);
  const clipUrl = `https://unipass.customs.go.kr/clip/hsinfosrch/openULS0201007Q.do?searchVal=${hsk}`;
  // FTA 포털은 검색이 POST/AJAX 라 psr.do 에 GET 파라미터를 줘도 prefill 안 됨(메인 노출).
  //   psrCategoryView.do 는 GET 으로 해당 협정·세번 PSR 원문(표)을 직접 반환 → 딥링크로 사용.
  const ftaPortal =
    ag && ag.ftaId
      ? `https://www.customs.go.kr/ftaportalkor/ad/ftaTrtyPsr/psrCategoryView.do?ftaId=${ag.ftaId}&nationId=&searchType=01&searchValue=${String(hsk).slice(0, 6)}`
      : "https://www.customs.go.kr/ftaportalkor/main.do";
  // PSR 표시행 — 백엔드 rows[](분류별 다중)·구버전 단일 응답 모두 수용.
  const psrRows =
    psr && psr.available
      ? psr.rows && psr.rows.length
        ? psr.rows
        : [
            {
              division: "",
              item_ko: psr.name_ko,
              criterion_ko: psr.psr_ko,
              item_en: psr.name_en,
              criterion_en: psr.psr_en,
            },
          ]
      : [];
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="audit-root"
        style={{
          background: "var(--audit-bg-elevated, #fff)",
          borderRadius: 10,
          maxWidth: 460,
          width: "100%",
          boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "12px 16px",
            background: "var(--audit-accent)",
            color: "#fff",
          }}
        >
          <span style={{ fontWeight: 700, fontSize: 14, flex: 1 }}>
            {isFta ? "FTA 협정세율" : "관세율"} 상세
          </span>
          <button
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              color: "#fff",
              fontSize: 18,
              cursor: "pointer",
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>
        <div style={{ padding: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 2 }}>
            {rate.label || rate.code}
          </div>
          <div
            style={{
              fontSize: 12,
              color: "var(--audit-text-muted)",
              marginBottom: 12,
            }}
          >
            품목번호 {formatHsk(hsk)} {rate.group ? `· ${rate.group}` : ""}
          </div>
          <table
            style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}
          >
            <tbody>
              <tr style={rowDividerStyle}>
                <td style={tdLabelStyle}>세율</td>
                <td
                  style={{
                    ...tdValueStyle,
                    fontWeight: 700,
                    color: "var(--audit-accent-dark)",
                  }}
                >
                  {val}
                </td>
              </tr>
              <tr style={rowDividerStyle}>
                <td style={tdLabelStyle}>부호</td>
                <td
                  style={{
                    ...tdValueStyle,
                    fontFamily: NUM_FONT,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {rate.code}
                </td>
              </tr>
              {ag && (
                <>
                  <tr style={rowDividerStyle}>
                    <td style={tdLabelStyle}>협정</td>
                    <td style={tdValueStyle}>{ag.name}</td>
                  </tr>
                  <tr style={rowDividerStyle}>
                    <td style={tdLabelStyle}>발효일</td>
                    <td style={tdValueStyle}>{ag.effective}</td>
                  </tr>
                  <tr style={rowDividerStyle}>
                    <td style={tdLabelStyle}>대상국가</td>
                    <td style={tdValueStyle}>{ag.members}</td>
                  </tr>
                  <tr style={rowDividerStyle}>
                    <td style={tdLabelStyle}>원산지증명</td>
                    <td style={tdValueStyle}>{ag.coMethod}</td>
                  </tr>
                  <tr style={rowDividerStyle}>
                    <td style={tdLabelStyle}>증명서 유효기간</td>
                    <td style={tdValueStyle}>{ag.coValid}</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
          {ag && ag.ftaId && (
            <div
              style={{
                marginTop: 14,
                border: "1px solid var(--audit-border)",
                borderRadius: 8,
                overflow: "hidden",
              }}
            >
              {/* 관세청 틀 — 헤더 띠 */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 12px",
                  background: "#eef3fb",
                  borderBottom: "1px solid var(--audit-border)",
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "var(--audit-text)",
                  }}
                >
                  원산지결정기준 (PSR)
                </span>
                <span
                  style={{
                    fontSize: 11.5,
                    color: "var(--audit-text-muted)",
                    fontFamily: NUM_FONT,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  제{String(hsk).slice(0, 4)}.{String(hsk).slice(4, 6)}호
                </span>
                <span
                  style={{
                    marginLeft: "auto",
                    fontSize: 10.5,
                    fontWeight: 700,
                    color: "#1a56c4",
                    background: "#dbe7fb",
                    border: "1px solid #a7c2f4",
                    borderRadius: 10,
                    padding: "1px 7px",
                  }}
                  title="관세청 FTA 포털 공식 데이터"
                >
                  관세청 원문
                </span>
              </div>

              <div style={{ padding: "10px 12px" }}>
                {psr === null ? (
                  <div
                    style={{ fontSize: 12, color: "var(--audit-text-muted)" }}
                  >
                    관세청 FTA 포털에서 불러오는 중…
                  </div>
                ) : psr.available ? (
                  psrRows.map((row, i) => {
                    const tags = classifyPsr(row.criterion_ko);
                    return (
                      <div
                        key={i}
                        style={{
                          paddingTop: i === 0 ? 0 : 10,
                          marginTop: i === 0 ? 0 : 10,
                          borderTop:
                            i === 0 ? "none" : "1px dashed var(--audit-border)",
                        }}
                      >
                        {psrRows.length > 1 && (
                          <div
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: "#1a56c4",
                              marginBottom: 4,
                            }}
                          >
                            선택 {row.division || i + 1}
                          </div>
                        )}
                        {(row.item_ko || row.item_en) && (
                          <div style={psrFieldRow}>
                            <span style={psrFieldLabel}>품목명</span>
                            <span style={psrFieldValue}>
                              <span>{row.item_ko}</span>
                              {row.item_en && (
                                <span style={psrEnStyle}>{row.item_en}</span>
                              )}
                            </span>
                          </div>
                        )}
                        {tags.length > 0 && (
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 4,
                              margin: "6px 0",
                            }}
                          >
                            {tags.map((tg) => {
                              const c = PSR_TONES[tg.tone] || PSR_TONES.gray;
                              return (
                                <span
                                  key={tg.code}
                                  title={tg.title}
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    padding: "2px 7px",
                                    borderRadius: 11,
                                    background: c.bg,
                                    color: c.fg,
                                    border: `1px solid ${c.bd}`,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {tg.label}
                                </span>
                              );
                            })}
                          </div>
                        )}
                        <div style={psrFieldRow}>
                          <span style={psrFieldLabel}>원산지기준</span>
                          <span style={psrFieldValue}>
                            <span>{row.criterion_ko || "기준 본문 없음"}</span>
                            {row.criterion_en && (
                              <span style={psrEnStyle}>{row.criterion_en}</span>
                            )}
                          </span>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div
                    style={{ fontSize: 12, color: "var(--audit-text-muted)" }}
                  >
                    이 협정·세번의 원산지결정기준이 관세청 포털에
                    없습니다(미게시). 아래 FTA 포털에서 확인하세요.
                  </div>
                )}
              </div>

              {psr && psr.available && (
                <div
                  style={{
                    padding: "6px 12px",
                    fontSize: 10.5,
                    color: "var(--audit-text-faint)",
                    borderTop: "1px solid var(--audit-border)",
                    background: "var(--audit-bg-subtle)",
                  }}
                >
                  출처: 관세청 FTA 포털 · 참고용 — 정확한 판정은 협정문/관세청
                  원문 기준
                </div>
              )}
            </div>
          )}
          <p
            style={{
              fontSize: 12.5,
              lineHeight: 1.6,
              color: "var(--audit-text-muted)",
              margin: "12px 0 0",
            }}
          >
            {isFta
              ? "FTA 협정세율은 원산지증명서·직접운송 등 협정 요건을 충족할 때 적용되며, 기본세율보다 우선합니다. 위 협정 정보는 관세청 공개정보 기준 참고용이며, 품목별 원산지결정기준(PSR)·정확한 요건·최신 개정은 아래 관세청 원문/FTA 포털에서 확인하세요."
              : "기본관세는 FTA 등 특별세율이 적용되지 않는 물품에 적용되는 일반세율입니다. 적용 우선순위·세부 내용은 아래 관세청 원문에서 확인하세요."}
          </p>
          <div
            style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}
          >
            <a
              href={clipUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={modalLinkBtn}
            >
              관세청 원문 ↗
            </a>
            {isFta && ag && ag.ftaId ? (
              <button
                type="button"
                onClick={() => setShowOriginal(true)}
                style={{ ...modalLinkBtn, cursor: "pointer" }}
              >
                관세청 PSR 원문 보기(해당 세번)
              </button>
            ) : isFta ? (
              <a
                href={ftaPortal}
                target="_blank"
                rel="noopener noreferrer"
                style={modalLinkBtn}
              >
                FTA 포털 ↗
              </a>
            ) : null}
          </div>
        </div>
      </div>
      {showOriginal && (
        <PsrOriginalView
          ftaId={ag.ftaId}
          agName={ag.name}
          hsk={hsk}
          rows={psrRows}
          onClose={() => setShowOriginal(false)}
        />
      )}
    </div>
  );
}

// 관세청 FTA 포털 PSR "원문" 을 앱 안에서 관세청-유사 표 UI 로 재현(시인성).
//   외부 raw 프래그먼트로 보내지 않고, 우리 데이터로 같은 표를 깔끔하게 렌더.
//   맨 아래에 "관세청에서 직접 보기" 외부 링크를 보조로 둔다.
function PsrOriginalView({ ftaId, agName, hsk, rows, onClose }) {
  const hs6 = String(hsk).slice(0, 6);
  const ch2 = hs6.slice(0, 2);
  const chName = (CHAPTERS_97.find((c) => c.ch === ch2) || {}).name || "";
  const rawUrl = `https://www.customs.go.kr/ftaportalkor/ad/ftaTrtyPsr/psrCategoryView.do?ftaId=${ftaId}&nationId=&searchType=01&searchValue=${hs6}`;
  const fmtHs6 = `${hs6.slice(0, 4)}.${hs6.slice(4, 6)}`;
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 2100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="audit-root"
        style={{
          background: "#fff",
          borderRadius: 10,
          maxWidth: 880,
          width: "100%",
          maxHeight: "88vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 14px 48px rgba(0,0,0,0.3)",
          overflow: "hidden",
        }}
      >
        {/* 관세청풍 헤더 띠 */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "13px 18px",
            background: "#2456a6",
            color: "#fff",
            flexShrink: 0,
          }}
        >
          <span style={{ fontWeight: 700, fontSize: 15, flex: 1 }}>
            원산지결정기준(PSR) 조회 · 관세청 FTA 포털
          </span>
          <button
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              color: "#fff",
              fontSize: 18,
              cursor: "pointer",
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>

        {/* 협정·품목 요약 */}
        <div
          style={{
            padding: "12px 18px",
            borderBottom: "1px solid #e3e6ec",
            background: "#f6f8fb",
            fontSize: 13,
            color: "#2b3340",
            flexShrink: 0,
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 14 }}>{agName}</div>
          <div style={{ marginTop: 3, color: "#5b6472" }}>
            품목번호{" "}
            <b
              style={{
                fontFamily: NUM_FONT,
                fontVariantNumeric: "tabular-nums",
                color: "#1a2330",
              }}
            >
              {fmtHs6}
            </b>
            {chName ? ` · 제${parseInt(ch2, 10)}류 ${chName}` : ""}
          </div>
        </div>

        {/* 관세청-유사 표 */}
        <div style={{ overflow: "auto", padding: "0 0 0 0" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 13,
            }}
          >
            <thead>
              <tr style={{ background: "#eef2f8" }}>
                <th style={psrThCell(110)}>
                  품목번호
                  <div style={psrThSub}>HS</div>
                </th>
                <th style={psrThCell(64)}>
                  분류
                  <div style={psrThSub}>Division</div>
                </th>
                <th style={psrThCell(null)}>
                  품목명
                  <div style={psrThSub}>Item</div>
                </th>
                <th style={psrThCell(null)}>
                  원산지기준
                  <div style={psrThSub}>Preference Criterion</div>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} style={{ borderTop: "1px solid #e3e6ec" }}>
                  <td style={psrTdMono}>{hs6}</td>
                  <td style={{ ...psrTd, textAlign: "center" }}>
                    {r.division || "-"}
                  </td>
                  <td style={psrTd}>
                    <div style={{ color: "#1a2330" }}>{r.item_ko}</div>
                    {r.item_en && <div style={psrTdEn}>{r.item_en}</div>}
                  </td>
                  <td style={psrTd}>
                    <div style={{ color: "#1a2330", lineHeight: 1.65 }}>
                      {r.criterion_ko || "-"}
                    </div>
                    {r.criterion_en && (
                      <div style={{ ...psrTdEn, marginTop: 4 }}>
                        {r.criterion_en}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 출처 + 외부 직접보기 */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "10px 18px",
            borderTop: "1px solid #e3e6ec",
            background: "#fafbfd",
            flexShrink: 0,
          }}
        >
          <span style={{ fontSize: 11.5, color: "#8a93a3", flex: 1 }}>
            출처: 관세청 FTA 포털 · 참고용 — 정확한 판정은 협정문/관세청 원문
            기준
          </span>
          <a
            href={rawUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: "#2456a6",
              textDecoration: "none",
              whiteSpace: "nowrap",
            }}
          >
            관세청에서 직접 보기 ↗
          </a>
        </div>
      </div>
    </div>
  );
}

const psrThCell = (w) => ({
  textAlign: "left",
  padding: "10px 14px",
  fontSize: 12.5,
  fontWeight: 700,
  color: "#2b3340",
  borderBottom: "2px solid #cdd6e4",
  width: w ? w : undefined,
  whiteSpace: "nowrap",
});
const psrThSub = {
  fontSize: 11,
  fontWeight: 600,
  color: "#8a93a3",
  marginTop: 1,
};
const psrTd = {
  padding: "11px 14px",
  verticalAlign: "top",
  color: "#2b3340",
  lineHeight: 1.55,
};
const psrTdMono = {
  ...psrTd,
  fontFamily: NUM_FONT,
  fontVariantNumeric: "tabular-nums",
  fontWeight: 700,
  color: "#1a2330",
  whiteSpace: "nowrap",
};
const psrTdEn = {
  fontSize: 12,
  color: "#7a8294",
  fontStyle: "italic",
  marginTop: 2,
  lineHeight: 1.5,
};

// 관세청 틀풍 PSR 필드 행 (라벨 좌 / 국·영 값 우)
const psrFieldRow = {
  display: "grid",
  gridTemplateColumns: "58px 1fr",
  gap: 8,
  alignItems: "start",
  padding: "3px 0",
};
const psrFieldLabel = {
  fontSize: 11,
  fontWeight: 700,
  color: "var(--audit-text-muted)",
  paddingTop: 1,
};
const psrFieldValue = {
  fontSize: 12.5,
  lineHeight: 1.6,
  color: "var(--audit-text)",
  whiteSpace: "pre-wrap",
  display: "flex",
  flexDirection: "column",
  gap: 2,
};
const psrEnStyle = {
  fontSize: 11.5,
  color: "var(--audit-text-muted)",
  fontStyle: "italic",
};

const modalLinkBtn = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  border: "1px solid var(--audit-accent)",
  color: "var(--audit-accent)",
  background: "var(--audit-bg)",
  borderRadius: 7,
  padding: "7px 12px",
  fontSize: 12.5,
  fontWeight: 600,
  textDecoration: "none",
};



/** "4205001" → "4205.00.1"  (4자리.2자리.나머지) */
function fmtCode(prefix) {
  if (!prefix) return "";
  const s = String(prefix).replace(/\./g, "");
  if (s.length <= 4) return s;
  if (s.length <= 6) return `${s.slice(0, 4)}.${s.slice(4)}`;
  return `${s.slice(0, 4)}.${s.slice(4, 6)}.${s.slice(6)}`;
}

/**
 * 국내세분 — national_group / national_tree 가 있을 때만 렌더.
 * - 그룹명 + 포맷코드 표시
 * - 형제 노드(같은 그룹 안 leaf/그룹) 목록, 현재 hsk 강조
 */
function SectionNationalGroup({ data }) {
  const ng = data.national_group;
  const tree = data.national_tree;
  if (!ng && !tree) return null;

  // 현재 hsk 가 속한 그룹노드를 tree 에서 찾아 children 반환
  const siblings = findSiblings(tree, data.hsk);

  return (
    <div style={cardStyle}>
      <h3 style={sectionTitleStyle}>국내세분</h3>
      {ng && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: siblings.length > 0 ? 10 : 0,
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              background: "var(--audit-accent)",
              color: "#fff",
              borderRadius: 3,
              padding: "1px 6px",
              letterSpacing: "0.3px",
            }}
          >
            국내 분류
          </span>
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "var(--audit-text)",
            }}
          >
            {ng.name}
          </span>
          <span
            style={{
              fontFamily: NUM_FONT,
              fontVariantNumeric: "tabular-nums",
              fontSize: 12,
              color: "var(--audit-text-muted)",
            }}
          >
            ({fmtCode(ng.prefix)})
          </span>
          {ng.source === "expired" && (
            <span
              style={{
                fontSize: 12,
                color: "var(--audit-text-faint)",
                fontStyle: "italic",
              }}
            >
              (개정 전 명칭)
            </span>
          )}
        </div>
      )}
      {siblings.length > 0 && (
        <div>
          <div
            style={{
              fontSize: 12,
              color: "var(--audit-text-muted)",
              marginBottom: 4,
              letterSpacing: "0.3px",
            }}
          >
            이 그룹의 세번
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {siblings.map((node) => {
              const isCurrent = node.is_leaf && node.hsk === data.hsk;
              const code = node.is_leaf
                ? formatHsk(node.hsk)
                : fmtCode(node.prefix);
              const label = node.is_leaf
                ? node.name_ko
                : node.label || node.name;
              return (
                <div
                  key={node.is_leaf ? node.hsk : node.prefix}
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "baseline",
                    padding: "2px 6px",
                    borderRadius: 3,
                    background: isCurrent
                      ? "var(--audit-accent-faint, rgba(26,115,232,0.10))"
                      : "transparent",
                    fontWeight: isCurrent ? 700 : 400,
                  }}
                >
                  <span
                    style={{
                      fontFamily: NUM_FONT,
                      fontVariantNumeric: "tabular-nums",
                      fontSize: 12,
                      color: isCurrent
                        ? "var(--audit-accent)"
                        : "var(--audit-text-muted)",
                      minWidth: 90,
                    }}
                  >
                    {code}
                  </span>
                  <span style={{ fontSize: 12, color: "var(--audit-text)" }}>
                    {label || "—"}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * national_tree 를 DFS 로 순회해 hsk 가 속한 그룹노드의 children 반환.
 * 그룹노드가 여러 depth 면 가장 가까운(말단) 그룹노드 children 반환.
 */
function findSiblings(tree, hsk) {
  if (!tree || !hsk) return [];
  function dfs(nodes) {
    for (const node of nodes) {
      if (!node.is_leaf && node.children) {
        // 이 그룹 children 중에 hsk 가 있는지 (직접 leaf 또는 하위 그룹)
        if (containsHsk(node.children, hsk)) {
          // 더 깊은 그룹이 있으면 그쪽 우선
          const deeper = dfs(node.children);
          return deeper.length > 0 ? deeper : node.children;
        }
      }
    }
    return [];
  }
  return dfs(tree);
}

function containsHsk(nodes, hsk) {
  for (const n of nodes) {
    if (n.is_leaf && n.hsk === hsk) return true;
    if (!n.is_leaf && n.children && containsHsk(n.children, hsk)) return true;
  }
  return false;
}

// ── P4 변경고시 주입 — 이 세번의 변경 이력·함정 경고 (amendment_trap_seed 현행 고시) ──
//    moved_out = 이 세번에서 다른 세번으로 변경된 품목(함정: 옛 세번으로 신고할 위험).
//    kwLast12m>0 = 고시 후에도 옛 세번 신고가 지속되는 진행형 함정 — 강한 경고.
//    moved_in = 변경고시로 이 세번에 분류된 품목(참고).
function AmendmentSourceLinks({ item, hsk, onOpenPdf }) {
  const evidence = amendmentEvidence(item);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
      <SourceEvidenceLink compact {...evidence.landing} />
      {evidence.pdf && item?.seqNo ? (
        <AmendmentPdfViewerButton hsk={hsk} item={item} onOpen={onOpenPdf} />
      ) : evidence.pdf ? (
        <SourceEvidenceLink compact {...evidence.pdf} />
      ) : null}
    </span>
  );
}

function AmendmentReason({ item }) {
  const reason = amendmentReasonText(item?.reason);
  if (!reason) return null;
  return (
    <div
      style={{
        marginTop: 4,
        paddingLeft: 12,
        color: "var(--audit-text-muted)",
        fontSize: 11.5,
        lineHeight: 1.55,
      }}
    >
      <b style={{ color: "var(--audit-text)" }}>변경사유</b> {reason}
      <span style={{ marginLeft: 6, whiteSpace: "nowrap" }}>· 국가법령정보센터 공식 PDF</span>
    </div>
  );
}

function SectionAmendment({ data, onOpenPdf }) {
  const am = data.amendment;
  if (!am || (!am.moved_out?.length && !am.moved_in?.length)) return null;
  const fmtCode = (c) => {
    const s = String(c || "").replace(/\D/g, "");
    return s.length >= 10 ? `${s.slice(0, 4)}.${s.slice(4, 6)}-${s.slice(6)}` : s;
  };
  const strong = am.trap_ongoing;
  return (
    <div
      style={{
        ...cardStyle,
        borderLeft: `4px solid ${strong ? "#d93025" : "#b06000"}`,
      }}
    >
      <h3 style={{ ...sectionTitleStyle, color: strong ? "#d93025" : "#b06000" }}>
        ⚠ 품목분류 변경고시 이력
      </h3>
      {am.moved_out?.length > 0 && (
        <div style={{ fontSize: 12.5, lineHeight: 1.6 }}>
          <div style={{ marginBottom: 4 }}>
            이 세번의 일부 품목이 변경고시로 <b>다른 세번으로 변경</b>되었습니다
            {strong && (
              <span style={{ color: "#d93025", fontWeight: 700 }}>
                {" "}
                — 최근 12개월에도 이 세번으로 신고가 지속되는 품목이 있어 주의가
                필요합니다
              </span>
            )}
            .
          </div>
          {am.moved_out.map((m, i) => (
            <div key={i} style={{ margin: "3px 0", color: "var(--audit-text)" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
                <span>
                  → <code style={{ fontWeight: 700 }}>{fmtCode(m.hsAfter)}</code>
                  <span style={{ color: "var(--audit-text-muted)" }}>
                    {" "}
                    · 고시 {m.gosiNo} ({m.gosiDate})
                    {Number(m.kwLast12m) > 0 && (
                      <span style={{ color: "#d93025" }}>
                        {" "}
                        · 최근 12개월 옛 세번 신고 {Number(m.kwLast12m).toLocaleString()}건
                      </span>
                    )}
                  </span>
                </span>
                <AmendmentSourceLinks item={m} hsk={data.hsk} onOpenPdf={onOpenPdf} />
              </span>
              {m.title && (
                <div style={{ fontSize: 11.5, color: "var(--audit-text-muted)" }}>
                  {m.title}
                  {m.seqNo ? ` · 연번 ${m.seqNo}` : ""}
                  {m.sourcePage ? ` · 현행 PDF ${m.sourcePage}쪽` : ""}
                </div>
              )}
              <AmendmentReason item={m} />
            </div>
          ))}
        </div>
      )}
      {am.moved_in?.length > 0 && (
        <div style={{ fontSize: 12.5, lineHeight: 1.6, marginTop: am.moved_out?.length ? 8 : 0 }}>
          <div style={{ color: "var(--audit-text-muted)", marginBottom: 2 }}>
            변경고시로 이 세번에 분류된 품목:
          </div>
          {am.moved_in.map((m, i) => (
            <div key={i} style={{ margin: "2px 0", color: "var(--audit-text-muted)" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
                <span>
                  <code>{fmtCode(m.hsBefore)}</code> 에서 · 고시 {m.gosiNo} ({m.gosiDate})
                  {m.title ? ` · ${m.title}` : ""}
                </span>
                <AmendmentSourceLinks item={m} hsk={data.hsk} onOpenPdf={onOpenPdf} />
              </span>
              <AmendmentReason item={m} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SectionOrigin({ data }) {
  const om = data.origin_marking || null;
  // 원문 리스트 마커(공백·중점·하이픈) 제거 — 'ㅇ' 표기는 관세 표기상 의미 있어 보존.
  const strip = (s) => (s || "").replace(/^[\s·-]+/, "");

  // null → 정보 미제공
  if (!om || !om.target) {
    return (
      <div style={cardStyle}>
        <h3 style={sectionTitleStyle}>원산지</h3>
        <div style={{ fontSize: 12, color: "var(--audit-text-muted)" }}>
          원산지표시 정보 미제공
        </div>
      </div>
    );
  }

  // N → 비대상 한 줄
  if (om.target === "N") {
    return (
      <div style={cardStyle}>
        <h3 style={sectionTitleStyle}>원산지</h3>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
          <span style={{ color: "var(--audit-text-muted)" }}>원산지표시대상</span>
          <span style={{ fontWeight: 600 }}>○ 비대상</span>
        </div>
        <div style={{ fontSize: 12, color: "var(--audit-text-muted)", marginTop: 4 }}>
          원산지표시 비대상 물품
        </div>
      </div>
    );
  }

  // Y → 표준 카드
  const clip = (u) => (u ? `https://unipass.customs.go.kr${u}` : null);
  const period = (b) =>
    b && b.valid_from ? `${b.valid_from}~${b.valid_to || ""}` : "";
  const methods = Array.isArray(om.methods) ? om.methods : [];
  // 근거고시: methods 의 basis 중 첫 유효 항목(대개 동일 고시), 없으면 target_basis
  const notice =
    (methods.find((m) => m.basis) || {}).basis || om.target_basis || null;

  return (
    <div style={cardStyle}>
      <h3 style={sectionTitleStyle}>원산지</h3>

      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, marginBottom: 10 }}>
        <span style={{ color: "var(--audit-text-muted)" }}>원산지표시대상</span>
        <span style={{ fontWeight: 700, color: "var(--audit-accent, #2563eb)" }}>● 대상</span>
      </div>

      {methods.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: "var(--audit-text)" }}>
            적정표시방법
          </div>
          {methods.map((m, i) => (
            <div key={i} style={{ marginBottom: 8, lineHeight: 1.5 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--audit-text)" }}>
                · {strip(m.goods) || "—"}
              </div>
              <div style={{ fontSize: 12.5, color: "var(--audit-text-muted)", paddingLeft: 12 }}>
                → {strip(m.method) || "—"}
              </div>
              {m.note && (
                <div style={{ fontSize: 11.5, color: "var(--audit-text-muted)", paddingLeft: 12, opacity: 0.85 }}>
                  [비고] {strip(m.note)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {notice && (
        <div style={{ fontSize: 11.5, color: "var(--audit-text-muted)", borderTop: "1px solid var(--audit-border, #e5e7eb)", paddingTop: 8, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          <span>근거 {notice.title} {period(notice) && `(${period(notice)})`}</span>
          {clip(notice.url) && (
            <a href={clip(notice.url)} target="_blank" rel="noreferrer"
               style={{ color: "var(--audit-accent, #2563eb)", textDecoration: "none" }}>
              관세청 원문 ↗
            </a>
          )}
        </div>
      )}
    </div>
  );
}


// ══ 한 장 표(문의 #104 · CLHS 대조) ═══════════════════════════════════════════

/** 섹션 하나 = 왼쪽 섹션 칸(rowspan) + 소항목 칸(k) + 내용. rows: [{k?, content, body?}] */
function SheetSection({ label, rows }) {
  const list = rows.filter(Boolean);
  if (!list.length) return null;
  return (
    <>
      {list.map((r, i) => (
        <tr key={i} className={i === list.length - 1 ? "end" : undefined}>
          {i === 0 && (
            <th className="sec" scope="row" rowSpan={list.length}>
              {label}
            </th>
          )}
          {r.k != null ? (
            <>
              <td className="k">{r.k}</td>
              <td className={r.body ? "body" : undefined}>{r.content}</td>
            </>
          ) : (
            <td colSpan={2} className={r.body ? "body" : undefined}>{r.content}</td>
          )}
        </tr>
      ))}
    </>
  );
}

function SheetHeader({ data }) {
  const formattedHsk = formatHsk(data.hsk);
  const googleQuery = [[data.name_ko, data.popum].filter(Boolean).join(" ").trim(), String(data.hsk || "")]
    .filter(Boolean)
    .join(" ");
  return (
    <div className="hsk-row">
      <span className="hsk-code">{formattedHsk}</span>
      <span className="hsk-muted">중량단위 <b>{data.unit_jung || "—"}</b></span>
      {/* 수량단위 — customs_hs_master.unit_su (구 하드코딩 "U" 는 와인 L·면직물 M2 등에서 오표시) */}
      <span className="hsk-muted">수량단위 <b>{data.unit_su || "—"}</b></span>
      <span className="hsk-faint" style={{ fontSize: 11.5 }}>관세청 CLIP 국내관세율 · 2026년 기준</span>
      <span className="hsk-actions">
        {/* 웹 교차확인은 우리만의 기능이라 맨 위에 둔다(사용자 결정 2026-09-26). 판정 아님 → 툴팁 */}
        <a
          className="hsk-btn"
          href={buildGoogleSearchUrl(googleQuery, "hs")}
          target="_blank"
          rel="noreferrer"
          title="외부 웹 검색은 교차확인용이며 공식 세번 판정이 아닙니다."
          onClick={(event) => {
            trackGoogleSearchAction("hs", "classification_result");
            if (getGoogleSearchOpenMode() === "popup" && openGoogleSearchPopup(googleQuery, "hs")) event.preventDefault();
          }}
        >
          웹 HS 사례 교차확인 ↗
        </a>
        <a
          className="hsk-btn"
          href={`https://unipass.customs.go.kr/clip/hsinfosrch/openULS0201007Q.do?searchVal=${data.hsk}`}
          target="_blank"
          rel="noopener noreferrer"
          title={`관세청 CLIP 007Q 원문 — "${formattedHsk}" 의 기본세율·협정세율·요건 (2026년 기준)`}
        >
          관세청 원문 확인 ↗
        </a>
      </span>
    </div>
  );
}

/** 세율 글자: 원문(raw)에 단위세액이 섞이면("487% 또는 956원") 원문 그대로, 아니면 N%. */
export function rateText(it) {
  const raw = it.raw ? String(it.raw).replace(/\s+/g, " ").trim() : "";
  if (raw && !/^[\d.]+%$/.test(raw)) return raw;
  if (it.value != null) return `${it.value}%`;
  return raw || "—";
}

/** 적용기간이 한 해 전체가 아니면 짧은 표시("~06.30", "05.01~", "03.01~06.30"). */
export function ratePeriodTag(it) {
  const a = String(it.strt || "").replace(/\D/g, "");
  const b = String(it.end || "").replace(/\D/g, "");
  if (a.length !== 8 && b.length !== 8) return null;
  const aStd = a.length !== 8 || /0101$/.test(a);
  const bStd = b.length !== 8 || /1231$/.test(b);
  if (aStd && bStd) return null;
  const md = (x) => `${x.slice(4, 6)}.${x.slice(6, 8)}`;
  return `${aStd ? "" : md(a)}~${bStd ? "" : md(b)}`;
}

/** 협정 이름 → 짧은 이름: "한ㆍ중미 FTA협정세율_코스타리카(선택1)" → "코스타리카", "RCEP협정세율_일본" → "일본". */
export function ftaShortName(label) {
  const full = String(label || "");
  let t = full.replace(/\(선택\d+\)/g, "");
  const under = t.split("_");
  if (under.length > 1) t = under[under.length - 1];
  t = t.replace(/^한\s*[ㆍ·.-]\s*/, "").replace(/(FTA\s*)?협정세율/g, "").replace(/\bFTA\b|\bCEPA\b/g, "").trim();
  if (t === "미") t = "미국";
  const opt = full.match(/\(선택(\d+)\)/);
  const name = t || full;
  return opt && opt[1] !== "1" ? `${name} 선택${opt[1]}` : name;
}

function RateLine({ it, name, onClick, on, best }) {
  const period = ratePeriodTag(it);
  const opt = String(it.label || "").match(/\(선택(\d+)\)/);
  return (
    <div
      className={`hsk-line${on ? " on" : ""}`}
      data-code={it.code}
      title={it.label || undefined}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
    >
      {name}({it.code}): <span className="hsk-rate">{rateText(it)}</span>
      {period && <span className="hsk-tag">{period}</span>}
      {it.applies_to ? <span className="hsk-tag">조건부</span> : opt && opt[1] !== "1" && <span className="hsk-tag">조건 확인</span>}
      {best && <span className="hsk-tag best">최저</span>}
      {/* 문의 #107: 선택세율은 품명 조건을 충족해야 받는다 — 관세청 협정관세율 상세의 세부 품명을 그대로 보인다. */}
      {it.applies_to && (
        <div className="hsk-cond" data-testid={`rate-cond-${it.code}`} style={{ color: "#5a6675", fontSize: 12, lineHeight: 1.5, paddingLeft: 12 }}>
          적용 대상: {it.applies_to}
        </div>
      )}
    </div>
  );
}

// 북한산(U) 세율은 지금 쓰이지 않아 화면에서 뺀다(문의 #75). 데이터·적용세율 계산은 그대로.
export const isHiddenRateCode = (code) => /^U\d*$/.test(String(code || ""));

export function SheetGeneralRates({ data, onRate }) {
  const [showPriority, setShowPriority] = useState(false);
  const basic = data.rates_grouped?.basic || null;
  const list = (data.rates_grouped?.general || []).filter((it) => !isHiddenRateCode(it.code));
  const rows = [...(basic ? [basic] : []), ...list];
  if (!rows.length) return <span className="hsk-muted">관세 데이터 없음</span>;
  return (
    <div>
      <div className="hsk-row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
        <div className="hsk-lines">
          {rows.map((it) => (
            <RateLine
              key={it.code}
              it={it}
              name={it.label || it.code}
              onClick={onRate ? () => onRate({ ...it, kind: "general" }) : undefined}
            />
          ))}
        </div>
        <button
          type="button"
          className="hsk-linkbtn"
          aria-expanded={showPriority}
          onClick={() => setShowPriority((v) => !v)}
        >
          [적용순서 안내]
        </button>
      </div>
      {showPriority && (
        <div className="hsk-prio">
          <table>
            <tbody>
              {TARIFF_PRIORITY.map((p) => (
                <tr key={p.order}>
                  <td className="o">{p.order}</td>
                  <td className="a">{p.apply}</td>
                  <td>{p.items.map((x) => `${x.name}(${x.code})`).join(" · ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="hsk-faint" style={{ fontSize: 11.5, padding: "3px 8px" }}>
            관세법 제50조. 같은 순위 안에서는 낮은 세율을 적용합니다.
          </div>
        </div>
      )}
    </div>
  );
}

const FTA_GROUPS = [
  ["europe", "유럽"],
  ["asia", "아시아"],
  ["middle", "중동"],
  ["rcep", "RCEP"],
  ["america", "미주"],
  ["other", "기타"],
];

/** FTA 칸 행들 — 첫 줄 원산지 선택(구 원산지별 적용세율), 다음 줄부터 지역별 "나라(코드): 세율". */
function ftaRows(data, onRate, origin, setOrigin) {
  const fta = data.rates_grouped?.fta || {};
  const groups = FTA_GROUPS.filter(([id]) => (fta[id] || []).length > 0);
  if (!groups.length) return [{ content: <span className="hsk-muted">FTA 협정 데이터 없음</span> }];
  const ratesFull = data.rates_full && Object.keys(data.rates_full).length ? data.rates_full : null;
  const result = origin && ratesFull ? computeApplicableRates(ratesFull, origin) : null;
  const onCodes = new Set((result?.applicable || []).map((a) => a.code));
  const bestCode = result?.recommended?.code || null;
  const rows = [];
  if (ratesFull) {
    rows.push({
      k: "원산지",
      content: (
        <div>
          <div className="hsk-row">
            <label htmlFor={`hsk-origin-${data.hsk}`} className="hsk-muted" style={{ fontSize: 12 }}>
              원산지를 고르면 해당 줄을 표시하고 가장 낮은 세율을 알려 줍니다
            </label>
            <select id={`hsk-origin-${data.hsk}`} value={origin} onChange={(e) => setOrigin(e.target.value)}>
              <option value="">선택…</option>
              {ORIGIN_COUNTRIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          {result && (
            <div className="hsk-result" aria-live="polite">
              {origin}: 적용 후보 {result.applicable.length}개
              {result.recommended ? (
                <> · 가장 낮은 세율 <b>{rateText(result.recommended)}</b> ({result.recommended.label || result.recommended.code})</>
              ) : " · 비교할 세율 없음"}
              {!result.has_origin_fta && " · 이 나라와의 FTA 없음"}
              <span className="hsk-faint"> · 관세법 제50조 순위 반영, 협정 요건(원산지증명 등) 충족 시</span>
            </div>
          )}
        </div>
      ),
    });
  }
  groups.forEach(([id, label]) => {
    rows.push({
      k: label,
      content: (
        <div className="hsk-lines">
          {fta[id].map((it, i) => (
            <RateLine
              key={`${it.code}-${i}`}
              it={it}
              name={ftaShortName(it.label || it.code)}
              on={onCodes.has(it.code)}
              best={bestCode === it.code}
              onClick={onRate ? () => onRate({ ...it, kind: "fta", group: label }) : undefined}
            />
          ))}
        </div>
      ),
    });
  });
  // 기본·WTO 가 최저면 관세 칸에 있으니 여기서 알려만 준다(위 결과 줄).
  return rows;
}






// ── 감면·환급 근거 팝업 — 제도 설명(간이정액/개별환급/감면) + 관세청 원문·법령 링크.
//    관세청 CLIP 납세/환급정보 시리즈(0401003Q 감면 · 005Q 분할납부 · 006Q 환급) 대응.
function RefundModal({ info, hsk, onClose }) {
  const clipUrl = `https://unipass.customs.go.kr/clip/hsinfosrch/openULS0201007Q.do?searchVal=${hsk}`;
  const secTitle = {
    fontSize: 12.5,
    fontWeight: 700,
    color: "var(--audit-text)",
    margin: "14px 0 4px",
  };
  const p = { fontSize: 12, color: "var(--audit-text)", lineHeight: 1.7, margin: 0 };
  const chip = {
    fontSize: 12,
    padding: "4px 10px",
    border: "1px solid var(--audit-border)",
    borderRadius: 12,
    color: "var(--audit-accent)",
    textDecoration: "none",
  };
  const chipStrong = { ...chip, border: "1px solid var(--audit-accent)", fontWeight: 700 };
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="audit-root"
        style={{
          background: "var(--audit-bg-elevated, #fff)",
          borderRadius: 10,
          maxWidth: 640,
          width: "100%",
          maxHeight: "84vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "12px 16px",
            background: "var(--audit-accent)",
            color: "#fff",
            flexShrink: 0,
          }}
        >
          <span style={{ fontWeight: 700, fontSize: 14, flex: 1 }}>
            감면·환급 근거 — {formatHsk(hsk)}
          </span>
          <button
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              color: "#fff",
              fontSize: 18,
              cursor: "pointer",
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>
        <div style={{ padding: 16, overflowY: "auto" }}>
          {/* 현재 품목 */}
          <div
            style={{
              padding: "10px 12px",
              border: "1px solid var(--audit-border)",
              borderRadius: 6,
              background: "var(--audit-bg-subtle, #f8fafc)",
              fontSize: 12.5,
              lineHeight: 1.7,
            }}
          >
            <div>
              간이정액환급:{" "}
              {info?.refund_amount ? (
                <b>
                  수출금액 1만원당 {info.refund_amount}
                  {info.refund_from && (
                    <span style={{ fontWeight: 400, color: "var(--audit-text-muted)" }}>
                      {" "}
                      (적용 {info.refund_from} ~ {info.refund_to || ""})
                    </span>
                  )}
                </b>
              ) : info ? (
                <span style={{ color: "var(--audit-text-muted)" }}>
                  대상 아님 (간이정액환급률표 미등재)
                </span>
              ) : (
                <span style={{ color: "var(--audit-text-muted)" }}>조회 중</span>
              )}
            </div>
            <div>
              감면:{" "}
              {info?.exempt_raw ? (
                <b>{info.exempt_raw}</b>
              ) : (
                <span style={{ color: "var(--audit-text-muted)" }}>
                  세번 지정 감면 표기 없음 — 용도·조건별 감면(아래)은 별도 신청
                </span>
              )}
            </div>
          </div>

          <div style={secTitle}>간이정액환급</div>
          <p style={p}>
            수출물품을 제조한 <b>중소기업</b>이 개별 서류 없이 <b>수출금액(FOB) 1만원당
            고시 정액</b>을 환급받는 제도입니다. 세번별 금액은 관세청이 고시하는{" "}
            <b>간이정액환급률표</b>에 따르며, 적용에는 직전 2년 환급실적 등 요건이 있습니다.
          </p>
          <div style={secTitle}>개별환급</div>
          <p style={p}>
            수출물품 제조에 사용된 <b>원재료 수입 시 납부한 관세</b>를 소요량 증빙으로
            산출해 환급받는 방식입니다. 간이정액환급 대상이 아니거나 실납부세액이 큰 경우
            사용합니다.
          </p>
          <div style={secTitle}>관세감면</div>
          <p style={p}>
            외교관용·학술연구용·재수출조건부 등 <b>용도와 조건</b>에 따라 신청하는
            감면(관세법 제88조~제101조)으로, 세번만으로 확정되지 않습니다. 분할납부·사후관리
            기간이 따르는 경우가 있습니다.{" "}
            <a href="/?audit=exemption" style={{ color: "var(--audit-accent)", fontWeight: 600 }}>
              → 관세감면 조문별 상세 안내
            </a>
          </p>

          <div style={secTitle}>근거 자료</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            <a href="https://unipass.customs.go.kr/clip/ctensrch/openULS0401006Q.do"
               target="_blank" rel="noopener noreferrer" style={chipStrong}>
              관세청 — 관세환급(간이정액·개별) 원문 ↗
            </a>
            <a href="https://unipass.customs.go.kr/clip/ctensrch/openULS0401003Q.do"
               target="_blank" rel="noopener noreferrer" style={chipStrong}>
              관세청 — 관세감면 원문 ↗
            </a>
            <a href="https://unipass.customs.go.kr/clip/ctensrch/openULS0401005Q.do"
               target="_blank" rel="noopener noreferrer" style={chip}>
              관세청 — 분할납부 원문 ↗
            </a>
            <a href="/?audit=installment"
               style={{ ...chip, color: "var(--audit-accent)", fontWeight: 600 }}>
              → 분할납부 상세 안내
            </a>
            <a href={clipUrl} target="_blank" rel="noopener noreferrer" style={chipStrong}>
              관세청 — 이 세번 원문(환급액 포함) ↗
            </a>
            <a href={encodeURI("https://www.law.go.kr/법령/관세법")}
               target="_blank" rel="noopener noreferrer" style={chip}>
              관세법 ↗
            </a>
            <a href={encodeURI("https://www.law.go.kr/법령/수출용원재료에대한관세등환급에관한특례법")}
               target="_blank" rel="noopener noreferrer" style={chip}>
              수출용 원재료 관세환급 특례법 ↗
            </a>
          </div>
          <div style={{ marginTop: 10, fontSize: 12, color: "var(--audit-text-faint)" }}>
            참고용 — 간이정액환급률표는 고시로 개정되므로 신고 시 현행 고시를 확인하세요.
            {info?.fetched_at && (
              <span> (관세청 조회: {String(info.fetched_at).slice(0, 10)})</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * 감면·환급 — backend refund_info(관세청 CLIP 세번별 화면 온디맨드 캐시) 렌더.
 * 간이정액환급액(수출 1만원당)·적용기간·감면 표기. null = 첫 조회 CLIP 지연(재조회 시 표시).
 */
function SectionRefund({ data }) {
  const ri = data?.refund_info;
  const [showModal, setShowModal] = useState(false);
  return (
    <div style={cardStyle}>
      <h3 style={sectionTitleStyle}>감면·환급</h3>
      <div
        onClick={() => setShowModal(true)}
        title="클릭 — 환급·감면 제도 안내와 관세청 근거"
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = "var(--audit-accent)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = "var(--audit-border)";
        }}
        style={{
          padding: "8px 10px",
          border: "1px solid var(--audit-border)",
          borderRadius: 6,
          background: "var(--audit-bg-subtle, #f8fafc)",
          fontSize: 12,
          lineHeight: 1.7,
          cursor: "pointer",
          transition: "border-color 0.1s",
        }}
      >
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontWeight: 700 }}>간이정액환급</span>
          {ri?.refund_amount ? (
            <span
              style={{
                marginLeft: "auto",
                fontFamily: NUM_FONT,
                fontVariantNumeric: "tabular-nums",
                fontWeight: 700,
                fontSize: 13,
                color: "var(--audit-text)",
              }}
            >
              {ri.refund_amount}{" "}
              <span style={{ fontWeight: 400, fontSize: 12, color: "var(--audit-text-muted)" }}>
                / 수출 1만원당
              </span>
            </span>
          ) : (
            <span style={{ marginLeft: "auto", color: "var(--audit-text-muted)" }}>
              {ri ? "대상 아님" : "조회 중 — 잠시 후 다시 열면 표시됩니다"}
            </span>
          )}
        </div>
        {ri?.refund_from && (
          <div style={{ color: "var(--audit-text-muted)", fontSize: 12 }}>
            적용기간 {ri.refund_from} ~ {ri.refund_to || ""}
          </div>
        )}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
          <div style={{ color: "var(--audit-text-muted)", flex: 1, minWidth: 0 }}>
            <span style={{ fontWeight: 600 }}>감면</span>
            {ri?.exempt_raw ? (
              ri.exempt_raw.split(" / ").map((e, i) => (
                <div
                  key={i}
                  style={{ marginTop: 3, paddingLeft: 12, textIndent: -12 }}
                >
                  · {e.trim()}
                </div>
              ))
            ) : (
              <span> : 세번 지정 표기 없음</span>
            )}
          </div>
          <span
            style={{
              color: "var(--audit-accent)",
              fontWeight: 600,
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            근거·제도 안내 →
          </span>
        </div>
      </div>
      {showModal && (
        <RefundModal info={ri} hsk={data?.hsk} onClose={() => setShowModal(false)} />
      )}
    </div>
  );
}

// ── 내국세 근거 팝업 — 세액 계산 구조 + 과세대상 전표(법 별표 큐레이션) + 법령·관세청 링크.
//    벤치마크(Ciel HS duty.asp 개별소비세 안내)의 "근거 제시" 요구 대응.
const _ITAX_LAWS = {
  excise: [
    ["개별소비세법", "https://www.law.go.kr/법령/개별소비세법"],
    ["개별소비세법 시행령", "https://www.law.go.kr/법령/개별소비세법시행령"],
    ["개별소비세법 시행규칙", "https://www.law.go.kr/법령/개별소비세법시행규칙"],
    ["교육세법", "https://www.law.go.kr/법령/교육세법"],
    ["농어촌특별세법", "https://www.law.go.kr/법령/농어촌특별세법"],
  ],
  transport: [
    ["교통·에너지·환경세법", "https://www.law.go.kr/법령/교통·에너지·환경세법"],
    ["교통·에너지·환경세법 시행령", "https://www.law.go.kr/법령/교통·에너지·환경세법시행령"],
    ["교육세법", "https://www.law.go.kr/법령/교육세법"],
  ],
  liquor: [
    ["주세법", "https://www.law.go.kr/법령/주세법"],
    ["주세법 시행령", "https://www.law.go.kr/법령/주세법시행령"],
    ["교육세법", "https://www.law.go.kr/법령/교육세법"],
  ],
};

function _itaxFormula(x) {
  const taxName = x.tax_label;
  const lines = [
    x.price_floor
      ? `① 과세표준 = (관세의 과세가격 + 관세) − 기준가격(${x.price_floor.replace(" 초과분", "")})`
      : "① 과세가격 = 관세의 과세가격(CIF) + 관세  ※ 종량세는 수량 기준",
    `② ${taxName} = ${x.price_floor ? "과세표준" : "과세가격(또는 수량)"} × ${x.flex_rate || x.base_rate}`,
  ];
  if (x.edu_rate) lines.push(`③ 교육세 = ${taxName}액 × ${x.edu_rate}`);
  if (x.farm_rate) lines.push(`④ 농어촌특별세 = ${taxName}액 × ${x.farm_rate}`);
  lines.push(
    `⑤ 부가가치세 = (과세가격 + 관세 + ${taxName}${x.edu_rate ? " + 교육세" : ""}${x.farm_rate ? " + 농특세" : ""}) × 10%`,
  );
  return lines;
}

function InternalTaxModal({ item, hsk, onClose }) {
  const [map, setMap] = useState(null); // null=로딩 / [] / rows
  useEffect(() => {
    let alive = true;
    fetch("/api/onto/internal-tax-map")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => alive && setMap(d?.items || []))
      .catch(() => alive && setMap([]));
    return () => {
      alive = false;
    };
  }, []);
  const clipUrl = `https://unipass.customs.go.kr/clip/hsinfosrch/openULS0201007Q.do?searchVal=${hsk}`;
  const laws = _ITAX_LAWS[item.tax_type] || _ITAX_LAWS.excise;
  const groups = useMemo(() => {
    const g = new Map();
    for (const r of map || []) {
      if (!g.has(r.tax_label)) g.set(r.tax_label, []);
      g.get(r.tax_label).push(r);
    }
    return [...g.entries()];
  }, [map]);
  const th = {
    textAlign: "left",
    padding: "5px 8px",
    borderBottom: "2px solid var(--audit-border)",
    whiteSpace: "nowrap",
    fontSize: 12,
    color: "var(--audit-text-muted)",
  };
  const td = {
    padding: "5px 8px",
    borderBottom: "1px solid var(--audit-border-subtle, #f1f3f5)",
    fontSize: 12,
    verticalAlign: "top",
  };
  const secTitle = {
    fontSize: 12.5,
    fontWeight: 700,
    color: "var(--audit-text)",
    margin: "16px 0 6px",
  };
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="audit-root"
        style={{
          background: "var(--audit-bg-elevated, #fff)",
          borderRadius: 10,
          maxWidth: 760,
          width: "100%",
          maxHeight: "84vh",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "12px 16px",
            background: "var(--audit-accent)",
            color: "#fff",
            flexShrink: 0,
          }}
        >
          <span style={{ fontWeight: 700, fontSize: 14, flex: 1 }}>
            내국세 근거 — {item.tax_label} · {item.target_name}
          </span>
          <button
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              color: "#fff",
              fontSize: 18,
              cursor: "pointer",
              lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>
        <div style={{ padding: 16, overflowY: "auto" }}>
          {/* 현재 품목 요약 */}
          <div
            style={{
              padding: "10px 12px",
              border: "1px solid var(--audit-border)",
              borderRadius: 6,
              background: "var(--audit-bg-subtle, #f8fafc)",
              fontSize: 12.5,
              lineHeight: 1.7,
            }}
          >
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
              <b>{formatHsk(hsk)}</b>
              <span>{item.target_name}</span>
              <span
                style={{
                  marginLeft: "auto",
                  fontFamily: NUM_FONT,
                  fontVariantNumeric: "tabular-nums",
                  fontWeight: 700,
                  fontSize: 14,
                }}
              >
                {item.flex_rate || item.base_rate}
              </span>
            </div>
            <div style={{ color: "var(--audit-text-muted)", fontSize: 12 }}>
              {item.flex_rate && <span>기본세율 {item.base_rate} → 탄력세율 적용 · </span>}
              {item.flex_valid && <span>적용기간 {item.flex_valid} · </span>}
              {item.edu_rate && <span>교육세 {item.edu_rate} · </span>}
              {item.farm_rate && <span>농특세 {item.farm_rate} · </span>}
              {item.rate_code && <span>세율부호 {item.rate_code}</span>}
            </div>
            {item.price_floor && (
              <div style={{ marginTop: 2, color: "#664d03", fontWeight: 600, fontSize: 12 }}>
                조건부 과세 — 기준가격 {item.price_floor}
              </div>
            )}
            {item.note && (
              <div style={{ marginTop: 2, color: "var(--audit-text-muted)", fontSize: 12 }}>
                {item.note}
              </div>
            )}
          </div>

          {/* 세액 계산 구조 */}
          <div style={secTitle}>세액 계산 구조</div>
          <div
            style={{
              fontFamily: NUM_FONT,
              fontVariantNumeric: "tabular-nums",
              fontSize: 12,
              lineHeight: 1.9,
              padding: "8px 12px",
              border: "1px solid var(--audit-border)",
              borderRadius: 6,
            }}
          >
            {_itaxFormula(item).map((l, i) => (
              <div key={i}>{l}</div>
            ))}
            <div style={{ color: "var(--audit-text-muted)", marginTop: 4 }}>
              세율 적용 순서: 잠정세율 → 탄력세율 → 기본세율
            </div>
          </div>

          {/* 과세대상 전표 */}
          <div style={secTitle}>
            과세대상·세율 전표{" "}
            <span style={{ fontWeight: 400, color: "var(--audit-text-muted)" }}>
              (법 별표 기준 · 현재 품목 강조)
            </span>
          </div>
          {map === null ? (
            <div style={{ fontSize: 12, color: "var(--audit-text-muted)" }}>불러오는 중…</div>
          ) : (
            groups.map(([label, rows]) => (
              <div key={label} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 700, margin: "6px 0 2px" }}>{label}</div>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={th}>과세대상</th>
                      <th style={th}>세율</th>
                      <th style={th}>교육세</th>
                      <th style={th}>농특세</th>
                      <th style={th}>기준가격</th>
                      <th style={th}>세율부호</th>
                      <th style={th}>HS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => {
                      const hit = String(hsk).startsWith(r.hs_prefix);
                      return (
                        <tr key={i} style={hit ? { background: "#fff3cd" } : undefined}>
                          <td style={{ ...td, fontWeight: hit ? 700 : 400 }}>{r.target_name}</td>
                          <td style={{ ...td, whiteSpace: "nowrap" }}>
                            {r.flex_rate ? (
                              <span>
                                {r.flex_rate}
                                <span style={{ color: "var(--audit-text-faint)" }}>
                                  {" "}
                                  (기본 {r.base_rate})
                                </span>
                              </span>
                            ) : (
                              r.base_rate
                            )}
                          </td>
                          <td style={td}>{r.edu_rate || "—"}</td>
                          <td style={td}>{r.farm_rate || "—"}</td>
                          <td style={td}>{r.price_floor || "—"}</td>
                          <td style={td}>{r.rate_code || "—"}</td>
                          <td style={{ ...td, fontFamily: NUM_FONT, fontVariantNumeric: "tabular-nums" }}>
                            {r.hs_prefix}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))
          )}

          {/* 근거 자료 링크 */}
          <div style={secTitle}>근거 자료</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {laws.map(([name, url]) => (
              <a
                key={name}
                href={encodeURI(url)}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: 12,
                  padding: "4px 10px",
                  border: "1px solid var(--audit-border)",
                  borderRadius: 12,
                  color: "var(--audit-accent)",
                  textDecoration: "none",
                }}
              >
                {name} ↗
              </a>
            ))}
            <a
              href="https://unipass.customs.go.kr/clip/ctensrch/openULS0401002Q.do"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: 12,
                padding: "4px 10px",
                border: "1px solid var(--audit-accent)",
                borderRadius: 12,
                color: "var(--audit-accent)",
                fontWeight: 700,
                textDecoration: "none",
              }}
            >
              관세청 — 내국세 등의 부과·징수 원문 ↗
            </a>
            <a
              href={clipUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: 12,
                padding: "4px 10px",
                border: "1px solid var(--audit-accent)",
                borderRadius: 12,
                color: "var(--audit-accent)",
                fontWeight: 700,
                textDecoration: "none",
              }}
            >
              관세청 — 이 세번 원문 ↗
            </a>
          </div>
          <div style={{ marginTop: 10, fontSize: 12, color: "var(--audit-text-faint)" }}>
            {item.law_ref && <span>적용 근거: {item.law_ref} · </span>}
            참고용 — 탄력세율은 수시 조정되므로 신고 시 현행 법령·고시를 확인하세요.
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * 62차: 부가세 면세 가능 안내 — 시행규칙 [별표 1]의 규격 문장(호에 걸린 줄 전부)·[별표 2] 수입 과세 줄·법령 링크.
 * "면세"라고 단정하지 않는다: 별표는 호+규격 문장이라 물품과 대조해야 하고 판단은 사용자(PROVE 원칙). 색은 파랑·회색만.
 */
export function VatExemptionNote({ ex }) {
  if (!ex) return null;
  const scopes = Array.isArray(ex.scopes) ? ex.scopes : [];
  const taxable = Array.isArray(ex.import_taxable) ? ex.import_taxable : [];
  const links = Array.isArray(ex.links) ? ex.links : [];
  const annex1 = ex.source?.annex1 || {};
  const annex2 = ex.source?.annex2 || {};
  const line = { fontSize: 12, lineHeight: 1.6, color: "var(--audit-text)", overflowWrap: "anywhere" };
  const tag = { fontWeight: 700, color: "var(--audit-text-muted)", marginRight: 6, whiteSpace: "nowrap" };
  return (
    <div data-testid="vat-exemption-note" style={{ borderLeft: "2px solid var(--audit-border)", paddingLeft: 10, display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ ...line, color: "var(--audit-text-muted)" }}>
        미가공식료품 면세 범위(부가가치세법 시행규칙 [별표 1])에 이 호가 있습니다. 아래 규격 문장에 맞는 물품만 면세이고, 판단은 신고인이 합니다.
      </div>
      {scopes.map((s, i) => (
        <div key={`s${i}`} data-testid="vat-exemption-scope" style={line}>
          <span style={tag}>[별표 1] {s.group_no != null ? `${s.group_no}. ` : ""}{s.group}{s.seq != null ? ` ${s.seq}` : ""}</span>
          {s.item}
        </div>
      ))}
      {taxable.map((s, i) => (
        <div key={`t${i}`} data-testid="vat-import-taxable" style={line}>
          <span style={tag}>[별표 2] 수입은 과세</span>
          {s.item}
          {ex.import_taxable_excluded_until && (
            <span style={{ color: "var(--audit-text-muted)" }}> — {ex.import_taxable_excluded_until}까지 수입하는 물품은 제외(제37조)</span>
          )}
        </div>
      ))}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "2px 10px", fontSize: 11.5, color: "var(--audit-text-muted)" }}>
        {links.map((l) => (
          <a key={l.url} href={encodeURI(l.url)} target="_blank" rel="noopener noreferrer" data-testid="vat-exemption-link" style={{ color: "#1d4ed8", textDecoration: "underline dotted", textUnderlineOffset: 2 }}>
            {l.label}
          </a>
        ))}
        {annex1.pdf && (
          <a href={annex1.pdf} target="_blank" rel="noopener noreferrer" style={{ color: "#1d4ed8", textDecoration: "underline dotted", textUnderlineOffset: 2 }}>
            [별표 1] 원문 PDF{annex1.amended ? ` (개정 ${annex1.amended})` : ""}
          </a>
        )}
        {taxable.length > 0 && annex2.pdf && (
          <a href={annex2.pdf} target="_blank" rel="noopener noreferrer" style={{ color: "#1d4ed8", textDecoration: "underline dotted", textUnderlineOffset: 2 }}>
            [별표 2] 원문 PDF
          </a>
        )}
      </div>
      {ex.note && <div style={{ fontSize: 11, color: "var(--audit-text-faint)", lineHeight: 1.5 }}>{ex.note}</div>}
    </div>
  );
}

/**
 * 내국세 — backend internal_tax {vat, items[]} 렌더 (Ciel HS answer 페이지 벤치마킹).
 * items 없으면 "개별소비세 해당 없음" 단정 표기. 탄력세율이 실적용가(크게), 기본세율은 보조.
 * 기준가격 사치품은 조건 뱃지(초과분 과세) — HS 만으로 확정 불가한 조건부 과세 명시.
 * 항목 클릭 → InternalTaxModal (계산 구조·과세대상 전표·법령/관세청 근거 링크).
 */
function SectionInternalTax({ data }) {
  const it = data?.internal_tax;
  const items = it?.items || [];
  const vatEx = it?.vat_exemption || null;
  const [detail, setDetail] = useState(null); // 클릭된 항목 → 근거 팝업
  return (
    <div style={cardStyle}>
      <h3 style={sectionTitleStyle}>내국세</h3>
      <table
        style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}
      >
        <tbody>
          <tr style={rowDividerStyle}>
            <td style={tdLabelStyle}>부가세</td>
            <td
              style={{
                ...tdValueStyle,
                fontFamily: NUM_FONT,
                fontVariantNumeric: "tabular-nums",
                textAlign: "right",
                width: vatEx ? undefined : 80,
              }}
            >
              {it?.vat || "10%"}
              {vatEx && (
                <span data-testid="vat-exemption-flag" style={{ marginLeft: 8, fontFamily: "inherit", fontWeight: 700, color: "#1d4ed8" }}>
                  면세 가능 · 미가공식료품
                </span>
              )}
            </td>
          </tr>
          {vatEx && (
            <tr>
              <td colSpan={2} style={{ padding: "4px 0 6px" }}>
                <VatExemptionNote ex={vatEx} />
              </td>
            </tr>
          )}
          {items.length === 0 && (
            <tr style={rowDividerStyle}>
              <td style={tdLabelStyle}>개별소비세</td>
              <td
                style={{
                  ...tdValueStyle,
                  textAlign: "right",
                  color: "var(--audit-text-muted)",
                }}
              >
                해당 없음
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {items.map((x, i) => (
        <div
          key={i}
          onClick={() => setDetail(x)}
          title="클릭 — 세액 계산 구조·과세대상 전표·법령 근거"
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = "var(--audit-accent)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = "var(--audit-border)";
          }}
          style={{
            marginTop: 8,
            padding: "8px 10px",
            border: "1px solid var(--audit-border)",
            borderRadius: 6,
            background: "var(--audit-bg-subtle, #f8fafc)",
            fontSize: 12,
            lineHeight: 1.6,
            cursor: "pointer",
            transition: "border-color 0.1s",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "var(--audit-text-muted)",
              }}
            >
              {x.tax_label}
            </span>
            <span style={{ fontWeight: 700, color: "var(--audit-text)" }}>
              {x.target_name}
            </span>
            <span
              style={{
                marginLeft: "auto",
                fontFamily: NUM_FONT,
                fontVariantNumeric: "tabular-nums",
                fontWeight: 700,
                fontSize: 13,
                color: "var(--audit-text)",
              }}
            >
              {x.flex_rate || x.base_rate}
            </span>
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "2px 10px",
              color: "var(--audit-text-muted)",
              fontSize: 12,
            }}
          >
            {x.flex_rate && <span>기본세율 {x.base_rate} → 탄력 적용</span>}
            {x.flex_valid && <span>적용기간 {x.flex_valid}</span>}
            {x.edu_rate && <span>교육세 {x.edu_rate}</span>}
            {x.farm_rate && <span>농특세 {x.farm_rate}</span>}
            {x.rate_code && <span>세율부호 {x.rate_code}</span>}
          </div>
          {x.price_floor && (
            <div
              style={{
                marginTop: 4,
                display: "inline-block",
                padding: "1px 8px",
                borderRadius: 3,
                background: "#fff3cd",
                color: "#664d03",
                fontWeight: 600,
                fontSize: 12,
              }}
            >
              조건부 과세 — 기준가격 {x.price_floor}
            </div>
          )}
          {x.note && (
            <div
              style={{
                marginTop: 3,
                color: "var(--audit-text-muted)",
                fontSize: 12,
              }}
            >
              {x.note}
            </div>
          )}
          {x.law_ref && (
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 8,
                color: "var(--audit-text-faint)",
                fontSize: 12,
              }}
            >
              <span>근거: {x.law_ref}</span>
              <span
                style={{
                  marginLeft: "auto",
                  color: "var(--audit-accent)",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                근거·전체 보기 →
              </span>
            </div>
          )}
        </div>
      ))}
      {items.length > 0 && (
        <div
          style={{
            marginTop: 6,
            fontSize: 12,
            color: "var(--audit-text-faint)",
          }}
        >
          참고용 — 탄력세율은 수시 조정되므로 신고 시 현행 법령·고시를 확인하세요.
        </div>
      )}
      {detail && (
        <InternalTaxModal
          item={detail}
          hsk={data?.hsk}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}

/**
 * 요건사항 섹션 — data.requirements JSONB (크롤 완료 후 채워짐) 우선,
 * 없으면 기존 law_requirements(customs_hs_law divi='S') fallback.
 * 둘 다 없으면 섹션 자체 미표시.
 */
// ── 요건 칸(한 장 표) ──────────────────────────────────────────────────────
// 세관장확인(0205 구조화)·통합공고 등(요건 JSON)·전략물자·기타·AI 가이드를 소항목 행으로 편다.
// 자료 고르기는 SectionRequirements 와 같다(displayLegacyRequirements·structuredCustomsItems).
const CUSTOMS_STATUS_CLASS = { active: "hsk-tag best", upcoming: "hsk-tag", expired: "hsk-tag hsk-faint" };

function CustomsCheckItem({ item }) {
  return (
    <div className="hsk-req-item" data-testid="customs-check-item">
      <div className="hsk-row" style={{ gap: "2px 8px" }}>
        <span className={CUSTOMS_STATUS_CLASS[item.status] || "hsk-tag"} style={{ marginLeft: 0 }}>{item.status_label}</span>
        <b>{item.document_name}</b>
        {item.related_law && <span className="hsk-muted" style={{ fontSize: 11.5 }}><LawLink law={item.related_law} muted /></span>}
        <span className="hsk-faint" style={{ fontSize: 11 }}>
          적용 {item.apply_start_date} ~ {item.apply_end_date || "종료일 없음"}
          {!item.observed_in_active_snapshot && " · 최신 목록 미관측(검증 이력 보존)"}
        </span>
      </div>
      <ReqLines text={item.text} laws={[item.related_law].filter(Boolean)} style={{ fontSize: 12.5, lineHeight: 1.6 }} />
      {Array.isArray(item.agencies) && item.agencies.length > 0 && (
        <div className="hsk-muted" style={{ fontSize: 12 }}>
          요건승인기관{" "}
          {item.agencies.map((agency, i) => (
            <React.Fragment key={agency.name || i}>
              {i > 0 && " · "}
              <AgencyLink ministry={agency.name} style={{ color: "var(--audit-text)", fontWeight: 600 }} />
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

function ReqItem({ item }) {
  return (
    <div className="hsk-req-item">
      <ReqLines
        text={item.text}
        laws={(item.agencies || []).map((a) => a.law).filter(Boolean)}
        style={{ fontSize: 12.5, lineHeight: 1.6, overflowWrap: "anywhere" }}
      />
      <ReqAgencyLine agencies={item.agencies} />
    </div>
  );
}

function reqLabelCell(label, hsk) {
  const cat = LABEL_TO_CAT[label];
  if (!cat) return label;
  return (
    <button type="button" className="hsk-linkbtn" style={{ fontSize: 12, color: "inherit" }} title="통관정보에서 보기" onClick={() => goReqGuide(cat, hsk)}>
      {label}
    </button>
  );
}

function requirementRows(data) {
  const req = displayLegacyRequirements(data);
  const customsGroups = customsChecksByDirection(data);
  const rows = [];
  const sourceUrl = data.customs_checks?.source_url;
  const asOf = data.customs_checks?.as_of;
  const customsNote = (
    <div className="hsk-faint" style={{ fontSize: 11 }}>
      {asOf ? `${asOf} 기준 · ` : ""}적용 중인 항목만 현재 통관 필수확인이며, 적용 종료 항목은 당시 이력입니다.
      {sourceUrl && (
        <> · <a href={sourceUrl} target="_blank" rel="noopener noreferrer" style={{ color: "var(--audit-accent)" }}>관세청 0205 원문 ↗</a></>
      )}
    </div>
  );
  let noted = false;
  const byLabel = (items) => {
    const m = new Map();
    items.forEach((it) => {
      const k = it.label || "기타";
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(it);
    });
    return [...m.entries()];
  };
  [["import", "수입"], ["export", "수출"]].forEach(([dir, dirLabel]) => {
    const customs = customsGroups[dir] || [];
    if (customs.length) {
      rows.push({
        k: `${dirLabel} 세관장확인`,
        content: (
          <div>
            {customs.map((item) => <CustomsCheckItem key={item.identity_key || item.record_id} item={item} />)}
            {!noted && customsNote}
          </div>
        ),
      });
      noted = true;
    }
    const legacy = Array.isArray(req?.[dir]) ? req[dir] : [];
    byLabel(legacy).forEach(([label, items]) => {
      rows.push({
        k: <>{dirLabel} {reqLabelCell(label, data.hsk)}</>,
        content: <div>{items.map((it, i) => <ReqItem key={i} item={it} />)}</div>,
      });
    });
    if (!customs.length && !legacy.length) rows.push({ k: dirLabel, content: <span className="hsk-muted">해당 없음</span> });
  });
  if (Array.isArray(req?.strategic) && req.strategic.length > 0) {
    rows.push({ k: "전략물자", content: <div className="hsk-lines">{req.strategic.map((x, i) => <div key={i} style={{ whiteSpace: "pre-wrap" }}>{x}</div>)}</div> });
  }
  if (req?.other != null && String(req.other).trim() !== "") {
    rows.push({ k: "기타", content: <div style={{ fontSize: 12.5, whiteSpace: "pre-wrap" }}>{req.other}</div> });
  }
  const laws = [];
  [...(req?.import || []), ...(req?.export || [])].forEach((x) =>
    (x.agencies || []).forEach((a) => { if (a.law && !laws.includes(a.law)) laws.push(a.law); }),
  );
  structuredCustomsItems(data).forEach((x) => { if (x.related_law && !laws.includes(x.related_law)) laws.push(x.related_law); });
  if (laws.length) rows.push({ k: "AI 가이드", content: <div>{laws.map((law) => <LawGuide key={law} law={law} />)}</div> });
  if (req?.agency_footer) rows.push({ k: "문의처", content: <span className="hsk-faint" style={{ fontSize: 11.5 }}>{req.agency_footer}</span> });
  // 요건 JSON·0205 가 없고 옛 law_requirements 표만 있으면 그 표를 그대로 한 칸에.
  if (!rows.some((r) => r.k !== "수입" && r.k !== "수출") && (data.law_requirements || []).length > 0) {
    return [{ content: <SectionRequirements data={data} />, body: true }];
  }
  return rows;
}

function SectionRequirements({ data }) {
  const req = displayLegacyRequirements(data);
  const customsItems = structuredCustomsItems(data);
  const customsGroups = customsChecksByDirection(data);
  const hasReq =
    customsItems.length > 0 ||
    (req &&
      ((Array.isArray(req.import) && req.import.length > 0) ||
      (Array.isArray(req.export) && req.export.length > 0) ||
      (Array.isArray(req.strategic) && req.strategic.length > 0) ||
        (req.other != null && String(req.other).trim() !== "")));

  // requirements JSONB 있으면 신규 구조로 렌더
  if (hasReq) {
    return (
      <div style={cardStyle}>
        <h3 style={sectionTitleStyle}>요건사항</h3>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {customsItems.length > 0 && (
            <CustomsChecksGroup
              groups={customsGroups}
              sourceUrl={data.customs_checks?.source_url}
              asOf={data.customs_checks?.as_of}
            />
          )}
          {Array.isArray(req?.import) && req.import.length > 0 && (
            <ReqGroup heading="수입" items={req.import} hsk={data.hsk} />
          )}
          {Array.isArray(req?.export) && req.export.length > 0 && (
            <ReqGroup heading="수출" items={req.export} hsk={data.hsk} />
          )}
          {(() => {
            const laws = [];
            [...(req?.import || []), ...(req?.export || [])].forEach((x) =>
              (x.agencies || []).forEach((a) => {
                if (a.law && !laws.includes(a.law)) laws.push(a.law);
              }),
            );
            customsItems.forEach((x) => {
              if (x.related_law && !laws.includes(x.related_law)) laws.push(x.related_law);
            });
            return laws.length ? (
              <div>
                <div style={reqSubHeadStyle}>AI 가이드</div>
                {laws.map((law) => (
                  <LawGuide key={law} law={law} />
                ))}
              </div>
            ) : null;
          })()}
          {Array.isArray(req?.strategic) && req.strategic.length > 0 && (
            <div>
              <div style={reqSubHeadStyle}>전략물자</div>
              <ul style={{ margin: "4px 0 0 0", paddingLeft: 18 }}>
                {req.strategic.map((s, i) => (
                  <li
                    key={i}
                    style={{
                      fontSize: 12,
                      color: "var(--audit-text)",
                      lineHeight: 1.6,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {req?.other != null && String(req.other).trim() !== "" && (
            <div>
              <div style={reqSubHeadStyle}>기타</div>
              <div
                style={{
                  fontSize: 12,
                  color: "var(--audit-text)",
                  lineHeight: 1.6,
                  whiteSpace: "pre-wrap",
                  marginTop: 4,
                }}
              >
                {req.other}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // fallback: law_requirements 테이블 (기존 구조)
  const items = data.law_requirements || [];
  if (items.length === 0) return null;

  return (
    <div style={cardStyle}>
      <h3 style={sectionTitleStyle}>수출입요건</h3>
      <table
        style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}
      >
        <thead>
          <tr style={{ background: "var(--audit-bg-subtle)" }}>
            <th style={thReqStyle(80)}>구분</th>
            <th style={thReqStyle(80)}>원산지</th>
            <th style={thReqStyle(null)}>품목명</th>
            <th style={thReqStyle(null)}>요건 / 비고</th>
            <th style={thReqStyle(90)}>적용일</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={i} style={rowDividerStyle}>
              <td
                style={{
                  ...tdValueStyle,
                  fontFamily: NUM_FONT,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {it.divi || "—"}
                {it.tariff_rate_divi && (
                  <span
                    style={{
                      color: "var(--audit-text-faint)",
                      fontSize: 12,
                      marginLeft: 4,
                    }}
                  >
                    /{it.tariff_rate_divi}
                  </span>
                )}
              </td>
              <td
                style={{
                  ...tdValueStyle,
                  fontFamily: NUM_FONT,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {it.origin_code || "—"}
              </td>
              <td style={{ ...tdValueStyle, lineHeight: 1.5 }}>
                {it.item_name || "—"}
              </td>
              <td
                style={{
                  ...tdValueStyle,
                  lineHeight: 1.5,
                  whiteSpace: "pre-wrap",
                  color: "var(--audit-text-muted)",
                }}
              >
                {it.im_requisite || "—"}
              </td>
              <td
                style={{
                  ...tdValueStyle,
                  fontFamily: NUM_FONT,
                  fontVariantNumeric: "tabular-nums",
                  color: "var(--audit-text-muted)",
                }}
              >
                {(it.apply_dt || "").slice(0, 8)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** 수입/수출 구분 블록 — label+text 쌍 목록 */
// 요건 라벨 → 통관정보 카테고리 매핑 (조회형만 링크; 그 외는 비클릭)
const LABEL_TO_CAT = {
  수출입공고: "customs_notice",
  통합공고: "integrated_notice",
  세관장확인: "customs_check",
};
function goReqGuide(cat, hsk) { openLink(reqGuideUrl(cat, hsk)); } // 65차: 설정(새 탭|같은 탭)대로

function ReqGroup({ heading, items, hsk }) {
  return (
    <div>
      <div style={reqSubHeadStyle}>{heading}</div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 6,
          marginTop: 6,
        }}
      >
        {items.map((item, i) => (
          <div
            key={i}
            style={{
              display: "grid",
              gridTemplateColumns: "90px 1fr",
              gap: "4px 8px",
              alignItems: "start",
            }}
          >
            <span
              onClick={
                LABEL_TO_CAT[item.label]
                  ? () => goReqGuide(LABEL_TO_CAT[item.label], hsk)
                  : undefined
              }
              title={LABEL_TO_CAT[item.label] ? "통관정보에서 보기" : undefined}
              style={{
                fontSize: 12,
                // 요건 유형별 폰트 색(문의 #23): 세관장확인=붉은색(통관 필수확인)·통합공고=청록·기타=회색.
                fontWeight:
                  item.label === "세관장확인" || item.label === "통합공고"
                    ? 700
                    : 600,
                color:
                  item.label === "세관장확인"
                    ? "#c0392b"
                    : item.label === "통합공고"
                      ? "#1d4ed8"
                      : "var(--audit-text-muted)",
                paddingTop: 1,
                wordBreak: "keep-all",
                overflowWrap: "anywhere",
                cursor: LABEL_TO_CAT[item.label] ? "pointer" : "default",
                textDecoration: LABEL_TO_CAT[item.label]
                  ? "underline dotted"
                  : "none",
              }}
            >
              {item.label || "—"}
            </span>
            <div style={{ minWidth: 0 }}>
              <ReqLines
                text={item.text}
                laws={(item.agencies || []).map((a) => a.law).filter(Boolean)}
                style={{ fontSize: 12, color: "var(--audit-text)", lineHeight: 1.65, overflowWrap: "anywhere" }}
              />
              <ReqAgencyLine agencies={item.agencies} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * 61차: 요건 항목의 근거 줄 — 법령(우리 법령별 통관정보 + 원문) · 소관부처(요건확인기관 연락처) · 직통전화(tel:).
 * Ciel HS 상세처럼 명사마다 근거 화면으로 이어진다. 자료는 항목의 agencies[](law.go.kr 담당부서 기준) 그대로.
 */
function ReqAgencyLine({ agencies }) {
  const rows = (Array.isArray(agencies) ? agencies : []).filter((a) => a && (a.law || a.ministry));
  if (!rows.length) return null;
  return (
    <div data-testid="req-agency-line" style={{ marginTop: 4, display: "flex", flexDirection: "column", gap: 2, fontSize: 11.5, color: "var(--audit-text-muted)", lineHeight: 1.6 }}>
      {rows.map((a, i) => (
        <div key={`${a.law || ""}|${a.ministry || ""}|${i}`} style={{ display: "flex", flexWrap: "wrap", gap: "0 6px", alignItems: "baseline" }}>
          {a.law && <LawLink law={a.law} />}
          {a.ministry && (
            <>
              {a.law && <span aria-hidden="true">·</span>}
              <AgencyLink ministry={a.ministry} style={{ color: "var(--audit-text-muted)" }} />
            </>
          )}
          {a.tel && (
            <>
              <span aria-hidden="true">·</span>
              <TelLink tel={a.tel} />
            </>
          )}
          {/* 법령 머리의 분야별 연락처 — 접어 둔다(문의 #77). */}
          {Array.isArray(a.contacts) && a.contacts.length > 1 && (
            <details data-testid="req-agency-contacts" style={{ flexBasis: "100%" }}>
              <summary style={{ cursor: "pointer" }}>분야별 문의처 {a.contacts.length}곳</summary>
              <AgencyContactList contacts={a.contacts} compact />
            </details>
          )}
        </div>
      ))}
    </div>
  );
}

function CustomsChecksGroup({ groups, sourceUrl, asOf }) {
  const directions = [
    ["import", "수입"],
    ["export", "수출"],
  ];
  const tone = {
    active: { color: "#b42318", background: "#fff1f0", border: "#fecdca" },
    upcoming: { color: "#92400e", background: "#fffbeb", border: "#fde68a" },
    expired: { color: "#667085", background: "#f2f4f7", border: "#d0d5dd" },
  };
  return (
    <div style={{ border: "1px solid var(--audit-border)", borderRadius: 6, overflow: "hidden" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 10,
          padding: "8px 10px",
          background: "var(--audit-bg-subtle)",
          borderBottom: "1px solid var(--audit-border)",
        }}
      >
        <div>
          <strong style={{ fontSize: 12, color: "var(--audit-text)" }}>세관장확인사항</strong>
          {asOf && (
            <span style={{ marginLeft: 7, fontSize: 11, color: "var(--audit-text-faint)" }}>
              {asOf} 기준
            </span>
          )}
        </div>
        {sourceUrl && (
          <a href={sourceUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: "#1d4ed8" }}>
            관세청 0205 원문 확인 ↗
          </a>
        )}
      </div>
      {directions.map(([key, label]) =>
        groups[key].length ? (
          <div key={key} style={{ padding: "9px 10px", borderBottom: "1px solid var(--audit-border)" }}>
            <div style={{ ...reqSubHeadStyle, marginBottom: 4 }}>{label}</div>
            {groups[key].map((item) => {
              const colors = tone[item.status] || tone.expired;
              return (
                <div key={item.identity_key || item.record_id} style={{ padding: "8px 0", borderBottom: "1px dashed var(--audit-border)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <span
                      style={{
                        fontSize: 10.5,
                        fontWeight: 800,
                        color: colors.color,
                        background: colors.background,
                        border: `1px solid ${colors.border}`,
                        borderRadius: 10,
                        padding: "1px 7px",
                      }}
                    >
                      {item.status_label}
                    </span>
                    <strong style={{ fontSize: 12, color: "var(--audit-text)" }}>{item.document_name}</strong>
                    {item.related_law && (
                      <span style={{ fontSize: 11, color: "var(--audit-text-muted)" }}>
                        <LawLink law={item.related_law} muted />
                      </span>
                    )}
                  </div>
                  <div style={{ marginTop: 4, fontSize: 11, color: "var(--audit-text-faint)" }}>
                    적용기간 {item.apply_start_date} ~ {item.apply_end_date || "종료일 없음"}
                    {!item.observed_in_active_snapshot && " · 최신 목록 미관측(검증 이력 보존)"}
                  </div>
                  <ReqLines
                    text={item.text}
                    laws={[item.related_law].filter(Boolean)}
                    style={{ marginTop: 5, fontSize: 12, lineHeight: 1.6, color: "var(--audit-text)" }}
                  />
                  {Array.isArray(item.agencies) && item.agencies.length > 0 && (
                    <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 10, fontWeight: 800, padding: "1px 6px", borderRadius: 4, background: "#eef4ff", color: "#1d4ed8", border: "1px solid #c7d7fe" }}>
                        요건승인기관
                      </span>
                      {item.agencies.map((agency) => (
                        <AgencyLink key={agency.name} ministry={agency.name} style={{ fontSize: 11.5, fontWeight: 700, color: "var(--audit-text)" }} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : null,
      )}
      <div style={{ padding: "7px 10px", fontSize: 10.5, color: "var(--audit-text-faint)" }}>
        적용 중인 항목만 현재 통관 필수확인으로 표시하며, 적용 종료 항목은 당시 이력입니다.
      </div>
    </div>
  );
}

const reqSubHeadStyle = {
  fontSize: 12,
  fontWeight: 700,
  color: "var(--audit-text-muted)", // 수입/수출 방향 색 구분 제거(의미 없음), 중립
  textTransform: "uppercase",
  letterSpacing: "0.4px",
  paddingBottom: 2,
  borderBottom: "1px solid var(--audit-border)",
};

const thReqStyle = (width) => ({
  textAlign: "left",
  padding: "5px 6px",
  fontSize: 12,
  fontWeight: 600,
  color: "var(--audit-text-muted)",
  width: width || undefined,
  borderBottom: "1px solid var(--audit-border-strong)",
});

// "4202111010" → "4202.11-1010"
function formatHsk(hsk) {
  if (!hsk || hsk.length !== 10) return hsk || "";
  return `${hsk.slice(0, 4)}.${hsk.slice(4, 6)}-${hsk.slice(6)}`;
}

const cardStyle = {
  border: "1px solid var(--audit-border)",
  background: "var(--audit-bg-elevated)",
  borderRadius: 6,
  padding: "12px 14px",
};

const sectionTitleStyle = {
  margin: "0 0 8px",
  fontSize: 13,
  fontWeight: 700,
  color: "var(--audit-text-muted)",
  textTransform: "uppercase",
  letterSpacing: 0,
};



const rowDividerStyle = {
  borderTop: "1px solid var(--audit-border)",
};



const tdLabelStyle = {
  padding: "6px 10px 6px 0",
  fontSize: 13,
  color: "var(--audit-text-muted)",
  width: 80,
  verticalAlign: "top",
};

const tdValueStyle = {
  padding: "6px 0",
  fontSize: 13,
  color: "var(--audit-text)",
};

const emptyStyle = {
  padding: 12,
  color: "var(--audit-text-muted)",
  fontSize: 12,
  fontStyle: "italic",
  border: "1px dashed var(--audit-border)",
  borderRadius: 4,
  background: "var(--audit-bg-subtle)",
};
